import React, { useRef, useState } from "react";
import { UploadCloud, FileCheck2, Info, AlertTriangle, CheckCircle2 } from "lucide-react";
import { cn } from "../../../../../utils/cn";
import { useTranslation } from "react-i18next";

// Campo etiquetado: label + ayuda + error. Bajo esfuerzo cognitivo: una sola
// columna, ayuda siempre visible en gris, error en rojo reemplaza la ayuda.
export function Field({ label, help, error, required, htmlFor, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={htmlFor} className="text-sm font-medium text-fg">
          {label}
          {required && <span className="text-status-error"> *</span>}
        </label>
      )}
      {children}
      {error ? (
        <span className="text-xs text-status-error leading-snug">{error}</span>
      ) : help ? (
        <span className="text-xs text-fg-muted leading-snug">{help}</span>
      ) : null}
    </div>
  );
}

// Zona de carga de archivos: arrastrar o hacer clic. Muestra el archivo elegido
// con su nombre y un botón para cambiarlo. Accesible por teclado.
export function FileDrop({ accept, file, onSelect, hint, disabled }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const { t } = useTranslation();

  const pick = (f) => {
    if (f && !disabled) onSelect(f);
  };

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        pick(e.dataTransfer.files?.[0]);
      }}
      className={cn(
        "flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors",
        disabled
          ? "opacity-50 cursor-not-allowed border-border"
          : "cursor-pointer hover:border-brand-500/60 hover:bg-bg-muted",
        drag ? "border-brand-500 bg-brand-50" : "border-border",
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        disabled={disabled}
        onChange={(e) => pick(e.target.files?.[0])}
      />
      {file ? (
        <>
          <FileCheck2 className="w-6 h-6 text-status-success" aria-hidden />
          <div className="text-sm font-medium text-fg break-all">{file.name}</div>
          <div className="text-xs text-fg-muted">{t("Haz clic para cambiar el archivo")}</div>
        </>
      ) : (
        <>
          <UploadCloud className="w-6 h-6 text-fg-subtle" aria-hidden />
          <div className="text-sm font-medium text-fg">{t("Arrastra el archivo o haz clic para subirlo")}</div>
          {hint && <div className="text-xs text-fg-muted">{hint}</div>}
        </>
      )}
    </div>
  );
}

const NOTE_STYLES = {
  info: { wrap: "bg-status-info-soft text-status-info", Icon: Info },
  warning: { wrap: "bg-status-warning-soft text-status-warning", Icon: AlertTriangle },
  success: { wrap: "bg-status-success-soft text-status-success", Icon: CheckCircle2 },
};

// Callout de contexto (info / advertencia / éxito). Se usa para las notas de
// honestidad ("esto no emite ninguna factura", "declaración bajo tu
// responsabilidad") y las referencias al proceso oficial.
export function InfoNote({ tone = "info", children }) {
  const s = NOTE_STYLES[tone] ?? NOTE_STYLES.info;
  const { Icon } = s;
  return (
    <div className={cn("flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-sm", s.wrap)}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
      <div className="leading-snug">{children}</div>
    </div>
  );
}
